import React, { useEffect, useState } from "react";
import Modal from "./Modal.tsx";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import SelectField from "../Forms/SelectField.js";
import { BasicDatePicker } from "../Forms/BasicDatePicker.js";
import { createShift } from "../../services/shifts.js";
import toast from "react-hot-toast";
import {
  capitalize,
  formatUKTime,
  formatUTCForDB,
  ukWallTimeToUTC,
} from "../../utils/functions.js";
import { getShifts } from "../../services/shifts.js";
import { WarningModal } from "./WarningModal.js";
import { getServiceUserMedicationGroups } from "../../services/users.js";

// Validation schema using Yup
export const validationSchema = Yup.object().shape({
  serviceUserId: Yup.string().required("Service user is required"),
  shiftStart: Yup.string().required("Start time is required"),
  shiftEnd: Yup.string()
    .required("End time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (value) {
        const { shiftStart } = this.parent;
        if (!shiftStart || !value) return true; // Skip validation if either is missing
        return new Date(value) > new Date(shiftStart);
      }
    ),
  shiftType: Yup.string().required("Shift type is required"),
  staffId: Yup.string().required("Staff member is required"),
  administerMedication: Yup.boolean().default(false),
  medicationGroupId: Yup.string().when("administerMedication", {
    is: true,
    then: (schema) => schema.required("Medication group is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export default function CreateShiftModal({
  isModalOpen,
  setIsModalOpen,
  reload,
  serviceUserOpt,
  staffOpt,
  initialShift,
  isCloning,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [medicationGroupOptions, setMedicationGroupOptions] = useState([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  const shiftTypeOpt = [
    {
      label: "Agency shift",
      value: "Agency shift",
    },
    {
      label: "Dom Care",
      value: "Dom Care",
    },
    {
      label: "Supported Living",
      value: "Supported Living",
    },
    {
      label: "Care Home",
      value: "Care Home",
    },
  ];

  const config = JSON.parse(localStorage.getItem("config"));
  const statusOpt = (config?.statuses || []).map((status) => {
    return {
      label: capitalize(status),
      value: status,
    };
  });

  const callOpt = (config?.calls || []).map((call) => {
    return {
      label: `${capitalize(call)} call`,
      value: call,
    };
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...initialShift,
      administerMedication: false,
    },
  });
  const startShift = watch("shiftStart");
  const shiftType = watch("shiftType");
  const serviceUserId = watch("serviceUserId");
  const administerMedication = watch("administerMedication");

  const handleConfirmProceed = async () => {
    setShowWarningModal(false);
    if (pendingFormData) {
      await processShiftCreation(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleConfirmCancel = () => {
    setShowWarningModal(false);
    setPendingFormData(null);
  };

  const processShiftCreation = async (data) => {
    if (!data.call) data.call = "none";

    // Picked wall times are UK local time (Europe/London), whatever the
    // booking device's timezone; persisted as UTC
    const shiftStartUTC = ukWallTimeToUTC(new Date(data.shiftStart));
    const shiftEndUTC = ukWallTimeToUTC(new Date(data.shiftEnd));

    // Clean up the data for API submission
    const payload = {
      ...data,
      shiftStart: formatUTCForDB(shiftStartUTC, "yyyy-MM-dd HH:mm:ss"),
      shiftEnd: formatUTCForDB(shiftEndUTC, "yyyy-MM-dd HH:mm:ss"),
    };

    // CCS-387: optional backdated "Created" date. Send it only when set;
    // otherwise omit so the backend keeps the default (created = now).
    if (data.createdAt) {
      payload.createdAt = formatUTCForDB(
        ukWallTimeToUTC(new Date(data.createdAt)),
        "yyyy-MM-dd HH:mm:ss"
      );
    } else {
      delete payload.createdAt;
    }

    // Remove fields that shouldn't be sent to API
    delete payload.administerMedication;
    // delete payload.medicationGroupId;

    setIsLoading(true);

    try {
      const { data: existingShifts } = await getShifts({
        serviceUserId: payload.serviceUserId,
        limit: 1000,
      });

      const conflict = existingShifts.find((existing) =>
        isOverlapping(
          shiftStartUTC,
          shiftEndUTC,
          new Date(existing.shiftStart),
          new Date(existing.shiftEnd)
        )
      );

      if (conflict) {
        toast.error(
          `Shift conflicts with an existing shift from ${formatUKTime(
            conflict.shiftStart,
            "dd/MM/yyyy HH:mm"
          )} to ${formatUKTime(conflict.shiftEnd, "dd/MM/yyyy HH:mm")}`
        );
        setIsLoading(false);
        return;
      }

      const res = await createShift(payload);
      if (res.status === 200) {
        toast.success("Shift created successfully");
      }
      setIsModalOpen(false);
      reload();
    } catch (error) {
      console.error("Error creating shift:", error);
      toast.error("Failed to create shift");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    // Check if medication should be administered but no group is selected
    if (!data.medicationGroupId || data.medicationGroupId === "") {
      setPendingFormData(data);
      setShowWarningModal(true);
      return;
    }

    // Proceed normally if medication group is selected or medication administration is false
    await processShiftCreation(data);
  };

  const isOverlapping = (newStart, newEnd, existingStart, existingEnd) => {
    return (
      new Date(newStart) <= new Date(existingEnd) &&
      new Date(newEnd) >= new Date(existingStart)
    );
  };
  const fetchMedicationGroups = async () => {
    setIsLoading(true);
    try {
      const { data } = await getServiceUserMedicationGroups(serviceUserId);
      return data;
    } catch (error) {
      console.error("Error fetching medication groups:", error);
      toast.error("Failed to fetch medication groups");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchGroups = async () => {
      setValue("medicationGroupId", "");
      setValue("administerMedication", false);
      if (serviceUserId) {
        const medGroups = await fetchMedicationGroups();
        if (medGroups && medGroups.length > 0) {
          const medGroupOptions = medGroups.map((group) => ({
            label: capitalize(group.name),
            value: group.id,
          }));
          setMedicationGroupOptions(medGroupOptions);
        } else {
          setMedicationGroupOptions([]);
        }
      } else {
        setMedicationGroupOptions([]);
      }
    };
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceUserId]);

  return (
    <Modal
      title={`${isCloning ? "Clone" : "Create"} shift`}
      onCancel={() => setIsModalOpen(false)}
      isOpen={isModalOpen}
      size="md"
    >
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SelectField
            label="Service user"
            options={serviceUserOpt}
            control={control}
            name="serviceUserId"
            error={errors.serviceUserId?.message}
            required
          />

          <SelectField
            label="Shift type"
            options={shiftTypeOpt}
            control={control}
            name="shiftType"
            error={errors.shiftType?.message}
            required
          />

          {shiftType === "Dom Care" && (
            <SelectField
              label="Call"
              options={callOpt}
              control={control}
              name="call"
              error={errors.call?.message}
            />
          )}

          <SelectField
            label="Administer medication"
            placeholder="Should medication be administered?"
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            control={control}
            name="administerMedication"
            error={errors.administerMedication?.message}
          />

          {administerMedication && (
            <SelectField
              label="Medication group"
              placeholder={
                serviceUserId
                  ? "Select medication group"
                  : "Select service user first"
              }
              options={medicationGroupOptions}
              control={control}
              name="medicationGroupId"
              error={errors.medicationGroupId?.message}
              required
            />
          )}

          <SelectField
            label="Status"
            options={statusOpt}
            control={control}
            name="status"
            error={errors.status?.message}
          />
          <SelectField
            label="Staff member"
            options={staffOpt}
            control={control}
            name="staffId"
            error={errors.staffId?.message}
            required
          />

          <Controller
            name="shiftStart"
            control={control}
            render={({ field }) => (
              <div className="form__group">
                <label>Start time</label>
                <BasicDatePicker
                  showTimeInput
                  height="48px"
                  timeInputLabel="Time"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Shift start time"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  /* CCS-387: no minDate — retrospective (past) shifts must be
                     allowed. start<end is still enforced by the end-time
                     minDate and the Yup schema. */
                  error={errors.shiftStart?.message}
                  required
                />
              </div>
            )}
          />

          <Controller
            name="shiftEnd"
            control={control}
            render={({ field }) => (
              <div className="form__group">
                <label>End time</label>
                <BasicDatePicker
                  height="48px"
                  timeInputLabel="Time"
                  value={field.value}
                  showTimeInput
                  onChange={field.onChange}
                  minDate={new Date(startShift)}
                  placeholder="Shift end time"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  error={errors.shiftEnd?.message}
                  required
                />
              </div>
            )}
          />

          {/* CCS-387: optional backdated "Created" date. Left blank => the
              shift's created date defaults to now. No minDate so it can be
              set in the past for retrospective entries. */}
          <Controller
            name="createdAt"
            control={control}
            render={({ field }) => (
              <div className="form__group">
                <label>Created date (optional)</label>
                <BasicDatePicker
                  height="48px"
                  timeInputLabel="Time"
                  value={field.value}
                  showTimeInput
                  onChange={field.onChange}
                  placeholder="Defaults to now if left blank"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  error={errors.createdAt?.message}
                />
              </div>
            )}
          />
          <button
            type="submit"
            className="btn btn__primary btn--block"
            disabled={isLoading}
          >
            {isLoading ? "Loading" : "Submit"}
          </button>
        </form>

        <WarningModal
          isOpen={showWarningModal}
          title="No Medication Group Selected"
          message="You have not selected a medication group. Are you sure you want to proceed without administering medication?"
          onConfirm={handleConfirmProceed}
          onCancel={handleConfirmCancel}
          isLoading={isLoading}
        />
      </>
    </Modal>
  );
}
